import { useMemo } from 'react';
import type { CSSProperties } from 'react';

import classNames from 'classnames';

import { Flex } from '@/shared/ui/Flex';

import styles from './Loading.module.css';
import { useLoadingSize } from './useLoadingSize';

export type LoadingSize = 'small' | 'medium' | 'large';
export type LoadingMode = 'determinate' | 'indeterminate';
export type LoadingColor = 'primary' | 'standard' | 'inverse';

export type LoadingProps = {
  className?: string;

  /**
   * @default 'small'
   */
  size?: LoadingSize;

  /**
   * @default 'determinate'
   */
  mode?: LoadingMode;

  /**
   * @default 'primary'
   */
  color?: LoadingColor;

  /**
   * mode값이 `determinate`일 경우 수동으로 loading의 progress를 조작하기 위한 값으로
   * 0 - 100 사이의 숫자로 default 값은 0 이다.
   *
   * @default 0
   */
  progress?: number;
};

// loading circle의 둘레값으로
// module에서 애니메이션을 위해서
const spinnerCircumference = 282;

const Loading = (props: LoadingProps) => {
  const { className, color = 'primary', mode = 'determinate', size = 'small', progress = 0, ...rest } = props;

  const loadingSize = useLoadingSize(size);

  const isDeterminate = mode === 'determinate';

  const circumference = useMemo(() => {
    if (!isDeterminate) return;

    const input = progress < 0 ? 0 : progress > 100 ? 100 : progress;

    return (input / 100) * spinnerCircumference;
  }, [isDeterminate, progress]);

  return (
    <Flex
      className={classNames(styles.loading, className)}
      align="center"
      justify="center"
      data-color={color}
      data-mode={mode}
      data-size={size}
      style={
        {
          ['--loading-determinate-dasharray']: circumference,
        } as CSSProperties
      }
      data-testid="opds-loading-root">
      <svg
        className={styles.svg}
        xmlns="http://www.w3.org/2000/svg"
        width={loadingSize}
        height={loadingSize}
        viewBox="0 0 100 100"
        data-testid="opds-loading-svg"
        {...rest}>
        {isDeterminate && (
          <circle
            className={classNames(styles.circle, styles.track)}
            cx={50}
            cy={50}
            r={45}
            data-testid="opds-loading-track"
          />
        )}
        <circle
          className={classNames(styles.circle, styles.spinner)}
          cx={50}
          cy={50}
          r={45}
          data-testid="opds-loading-spinner"
        />
      </svg>
    </Flex>
  );
};

export default Loading;
